import { isEmpty, isPlainObject } from 'lodash';

import { resolveTemplateValue, type TemplateSyntax } from './data-parser';
import transformKeyToJsonLogic from './transformKeysToJsonLogic';

export const buildSearchJsonLogic = (
  searchableFields: string[],
  searchValue: string
) => {
  const jsonLogicItems = searchableFields.map(field =>
    transformKeyToJsonLogic(field, searchValue, true)
  );
  return {
    or: jsonLogicItems,
  };
};

const isPresentFilter = (filter: unknown): boolean =>
  !!filter && !(isPlainObject(filter) && isEmpty(filter));

/**
 * Combines JSONLogic filters with AND and serializes them for the BE `filters`
 * param. Empty filters are dropped; returns `undefined` when nothing is left.
 * Strips the `@.` prefix authors use in configured filters (BE resolves vars
 * against the instance data).
 */
export const stringifyFilters = (filters: unknown[]): string | undefined => {
  const present = filters.filter(isPresentFilter);
  if (present.length === 0) return undefined;

  const combined = present.length === 1 ? present[0] : { and: present };
  return JSON.stringify(combined).replace(/@\./g, '');
};

/**
 * Builds the BE `filters` param for model-instances endpoints.
 *
 * @param staticFilters - Filters configured in the schema. `{{...}}` templates
 * in them are resolved against `context`.
 * @param extraFilters - Filters built in code (search, user selection). Used as
 * is, so user-typed text is never interpreted as a template.
 * @param context - Data used to resolve the templates (e.g. `usePage().context`).
 * @param syntax - `default` for `{{...}}`; `runtime` for `{%...%}` (Formily fields).
 */
export const buildFiltersParam = ({
  staticFilters = [],
  extraFilters = [],
  context = {},
  syntax,
}: {
  staticFilters?: unknown[];
  extraFilters?: unknown[];
  context?: Object;
  syntax?: TemplateSyntax;
}): string | undefined =>
  stringifyFilters([
    ...staticFilters.map(filter =>
      isPresentFilter(filter)
        ? resolveTemplateValue(filter, context, syntax)
        : filter
    ),
    ...extraFilters,
  ]);
